import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Layout } from "./components/layout/Layout";
import { CookieConsentBanner } from "./components/layout/CookieConsentBanner";
import { FEATURES } from "./config/features";
import { hasAcceptedTracking } from "./config/analytics";
import { initializeAnalytics, trackPageView } from "./services/analytics";

// Pages
import Home from "./pages/Home";
import RecordSuspensions from "./pages/RecordSuspensions";
import USEntryWaivers from "./pages/USEntryWaivers";
import Dubai from "./pages/Dubai";
import Eligibility from "./pages/Eligibility";
import ThankYou from "./pages/ThankYou";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";

// V2 Pages (placeholder/conditional)
import CaseArchiveComingSoon from "./pages/CaseArchiveComingSoon";
import CaseArchive from "./pages/CaseArchive";
import AdminNotFound from "./pages/AdminNotFound";
import SystemCheck from "./pages/SystemCheck";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Page tracking component (must be inside Router)
const PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (!hasAcceptedTracking()) return;

    // Small delay to ensure page title updates
    const timeoutId = setTimeout(() => {
      trackPageView(location.pathname, document.title);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  return null;
};

function App() {
  // Initialize analytics on mount if consent already given
  useEffect(() => {
    if (hasAcceptedTracking()) {
      initializeAnalytics();
    }
  }, []);

  return (
    <HelmetProvider>
      <div className="App dark">
        <BrowserRouter>
          <ScrollToTop />
          <PageTracker />
          <Layout>
            <Routes>
              {/* Core Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/record-suspensions" element={<RecordSuspensions />} />
              <Route path="/us-entry-waivers" element={<USEntryWaivers />} />
              <Route path="/international-filings" element={<Dubai />} />
              <Route path="/eligibility" element={<Eligibility />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              
              {/* V2 Routes - Conditional */}
              <Route 
                path="/case-archive" 
                element={FEATURES.V2_ENABLE_CASE_ARCHIVE ? <CaseArchive /> : <CaseArchiveComingSoon />} 
              />
              <Route 
                path="/admin" 
                element={<AdminNotFound />} 
              />
              {FEATURES.V2_ENABLE_SYSTEM_CHECK && (
                <Route path="/system-check" element={<SystemCheck />} />
              )}
              
              {/* Catch-all 404 */}
              <Route path="*" element={<AdminNotFound />} />
            </Routes>
          </Layout>
          <CookieConsentBanner />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
